/**
 * Timeout and limit constants for browser operations.
 *
 * @module constants
 */

// Timeout constants for browser operations (in milliseconds)
export const TIMEOUTS = {
  NAVIGATE: 30_000,
  GET_TITLE: 20_000,
  GET_LINKS: 20_000,
  SNAPSHOT: 130_000,
  // Driver-side hard cap for a single `page.ariaSnapshot()` call. ariaSnapshot
  // has NO Playwright timeout and ignores AbortSignal: on a jammed renderer
  // (blocked main thread / native dialog) the op parks for minutes, and the
  // per-tool timeout's abort can't cancel it — the orphan kept running until
  // the 1800s job-stall watchdog tore down the whole job (prod, June 2026).
  // This cap makes the *driver* give up and fall back to an empty snapshot so
  // the agent keeps moving.
  //
  // Sized for SLOW heavy pages, not just dead ones: a Blazor/heavy-DOM page can
  // legitimately take tens of seconds to produce the a11y tree, and a too-low
  // cap was misreading "slow" as "wedged" and triggering destructive context
  // recovery (prod SEV1, June 2026 — auth loss + wire-error storm). At 2 min the
  // cap only fires on a genuinely parked renderer; a slow-but-alive page now
  // finishes. Kept below POST_ACTION_SNAPSHOT (125s) and SNAPSHOT (130s) so the
  // inner cap still wins; a healthy ariaSnapshot returns in <1s.
  ARIA_SNAPSHOT_CAP: 120_000,
  CLICK: 10_000,
  TYPE: 10_000,
  FILL_FORM: 20_000,
  EVALUATE: 20_000,
  SCREENSHOT: 10_000,
  WAIT_FOR: 20_000,
  // Post-action stabilization timeouts
  STABILIZE_NAV: 5_000, // Network idle after navigation
  STABILIZE_INTERACTION: 1_500, // Network idle after click/submit
  STABILIZE_DOM: 150, // DOM/animation settle time
  // Exploration-level timeout (caps the entire navigation exploration phase)
  NAVIGATION_EXPLORATION: 15 * 60_000, // 15 minutes
  // Per-tool-call hard ceilings. These bound the entire tool execution
  // (gate wait + executor body + post-action snapshot). They exist to keep a
  // single hung Playwright op from consuming the 30-min stall watchdog budget
  // — when the inner promise never settles (blocked dialog, wedged JS context,
  // etc.) we surface a recoverable error so the agent can try snapshot/dialog
  // recovery and the segment can fail cleanly with its video intact.
  //
  // Invariant: POST_ACTION_SNAPSHOT < TOOL_INTERACTION. The inner snapshot
  // bound must fire before the outer tool bound so a hung AFTER-snapshot
  // degrades to a synthetic diff (action result preserved) rather than a
  // bare tool-timeout recovery string (action result lost).
  //
  // Sized for slow heavy pages (June 2026): actions and observation on a busy
  // Blazor/heavy-DOM page can take far longer than the old 30–60s ceilings while
  // the page is perfectly alive. These budgets bound a genuinely hung op (the
  // ~28-min step deadline and crash/close events are the real backstops) without
  // mistaking ordinary slowness for a wedge. On expiry the agent gets a recovery
  // string and re-observes — the page is NOT torn down.
  TOOL_DEFAULT: 150_000,
  TOOL_INTERACTION: 150_000, // click, type, hover, drag, press_key, dismiss_overlay
  TOOL_NAVIGATE: 160_000,
  TOOL_HANDLE_DIALOG: 5_000,
  POST_ACTION_SNAPSHOT: 125_000,
  // Hard cap for the best-effort `window.stop()` dispatched on per-tool-timeout
  // abort. Bounds wedged JS contexts; a healthy page returns in <10ms.
  UNWEDGE_PAGE_CAP: 1_000,
  // Driver-side grace added on top of `__waitDomQuiet`'s in-page maxWaitMs
  // before the evaluate itself is abandoned. The in-page cap is a page-side
  // setTimeout that never fires on a blocked main thread; the driver-side
  // race (maxWaitMs + this grace) guarantees forward progress regardless.
  WAIT_DOM_QUIET_EVAL_GRACE: 500,
  // Driver-side cap for the liveness probe (`page.evaluate(() => 1)`). NOTE: the
  // per-tool-timeout / snapshot-timeout recovery that consumed this was removed
  // June 2026 (a 750ms probe read slow heavy pages as dead and tore them down —
  // prod SEV1). `probeLiveness` is retained as dormant, tested scaffolding for a
  // future, reliable recovery (see TODO(browser-recovery)); nothing in the live
  // path calls it today.
  LIVENESS_PROBE_CAP: 750,
  // A captured request still 'pending' (no response headers) past this age is
  // ignored by the HTTP-settle stabilization signal — a long-poll / hung XHR /
  // header-less streaming fetch must never block a snapshot indefinitely.
  HTTP_SETTLE_MAX_AGE: 10_000,
} as const;

// Pointer-event drag gesture pacing (see PlaywrightClient.performPointerDrag).
// Pointer-sensor DnD libraries (dnd-kit, react-aria, pragmatic-dnd) gate drag
// activation on post-mousedown movement and track the pointer with
// rAF-throttled collision detection — the gesture must move in paced steps and
// hold before release or the drop resolves at the activation position.
export const POINTER_DRAG = {
  /** Px moved right after mousedown to satisfy distance-activation constraints. */
  ACTIVATION_NUDGE_PX: 8,
  /** Pause after the nudge so the library mounts its drag state (ms). */
  ACTIVATION_HOLD_MS: 80,
  /** Intermediate pointer positions walked between start and target. */
  PATH_STEPS: 10,
  /** Pause between steps so rAF-throttled dragover/collision logic keeps up (ms). */
  STEP_DELAY_MS: 25,
  /** Hold over the target before mouseup so the final slot is registered (ms). */
  DROP_HOLD_MS: 120,
} as const;

// Above this many interactive refs, a page is "large": per-ref scroll-position
// hints (the horizontal-clip `~hclip` pass) are skipped to keep augmentation
// lean, and the executor emits a `[Large page: …]` marker telling the agent to
// narrow or scroll instead. Tuned so a dense grouped/list view (≈hundreds of
// cards) trips it while a paginated table stays well under.
export const LARGE_PAGE_REF_THRESHOLD = 1_500;

// Default crawler limits
export const DEFAULTS = {
  MAX_DEPTH: 10,
  MAX_PAGES: 100,
  DELAY_MS: 500,
} as const;
